/**
 * Gedeelde beweging voor de hero's.
 *
 * De homepage, de subpagina's, het uitgelichte blok, het afbeelding-tekstblok en de
 * vacaturepagina lieten hun kop alle vijf op dezelfde manier binnenkomen: regel voor regel
 * van onder een masker vandaan, met dezelfde easing. Die code stond vijf keer, met vijf
 * keer dezelfde twee zwakke plekken erin. Nu staat hij hier.
 *
 * De twee zwakke plekken, en waarom het hier anders gaat:
 *
 * 1. **Splitsen wacht op het lettertype.** SplitText meet regelafbrekingen. Doet hij dat
 *    terwijl Larken nog laadt, dan meet hij de fallback -- en levert dan soms nul regels
 *    op, waarna er met de kop niets gebeurt. Dat was geen theorie: op de vacaturepagina
 *    kwam de titel per keer wisselend wel of niet binnen.
 * 2. **fromTo() in plaats van from().** Een from()-tween leest zijn eindwaarde af op het
 *    moment dat hij wordt aangemaakt. Wordt hij opnieuw opgebouwd nadat de begintoestand
 *    al stond -- wat bij autoSplit gebeurt -- dan legt hij die begintoestand vast als
 *    eindtoestand, en blijft de tekst onzichtbaar.
 *
 * Omdat het splitsen nu wacht, zou je de kop heel even compleet zien staan voordat hij
 * zich opbouwt. Daarom gaat hij meteen op onzichtbaar en pas in onSplit weer aan -- op dat
 * moment staan de regels al onder hun masker.
 *
 * Progressive enhancement: zonder JS -- of met prefers-reduced-motion -- staat alles gewoon
 * op zijn plek. Gaat er onverhoopt iets mis, dan gaat de kop weer aan: een kop die niet
 * beweegt is een schoonheidsfout, een onzichtbare kop is een kapotte pagina.
 */

import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(SplitText, CustomEase);

if (!CustomEase.get('osmo')) {
  CustomEase.create('osmo', 'M0,0 C0.625,0.05 0,1 1,1');
}

/**
 * De easing van dit thema: snel op gang, lang uitrollend. Eén naam, zodat alles wat
 * beweegt dezelfde hand heeft.
 */
export const EASE = 'osmo';

/**
 * Of de bezoeker gevraagd heeft om minder beweging.
 *
 * @returns {boolean}
 */
export function minderBeweging() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Wacht tot de lettertypen binnen zijn.
 *
 * Valt terug op een opgeloste belofte waar document.fonts niet bestaat, zodat een oude
 * browser de kop gewoon meteen opbouwt in plaats van hem nooit te tonen.
 *
 * @returns {Promise<void>}
 */
function lettertypenGereed() {
  const fonts = document.fonts;

  return fonts && fonts.ready ? fonts.ready.then(() => undefined) : Promise.resolve();
}

/**
 * Splitst een element in regels en laat die van onder hun masker vandaan komen.
 *
 * mask: 'lines' geeft elke regel een eigen masker, zodat de tekst eronder vandaan komt in
 * plaats van simpelweg te faden. autoSplit hersplitst bij een resize en zodra het
 * lettertype wisselt -- onSplit draait dan opnieuw, dus de animatie hoort daarin.
 *
 * naSplit krijgt het element mee zodra de regels onder hun masker staan maar de animatie
 * nog niet gelopen heeft: het moment waarop de inhoud ongezien te verbouwen valt. Het
 * draait bij elke hersplitsing opnieuw, dus het moet herhaalbaar zijn.
 *
 * @param {Element|null} element
 * @param {object}       [opties]
 * @param {string}       [opties.klasse]        class op elke regel; de bijbehorende
 *                                              `-mask` krijgt in de SCSS de ruimte voor
 *                                              staarten van een g, j, p, q of y
 * @param {number}       [opties.delay]
 * @param {number}       [opties.duration]
 * @param {number}       [opties.stagger]
 * @param {boolean}      [opties.fade]          ook infaden in plaats van alleen schuiven
 * @param {object}       [opties.scrollTrigger] voor koppen die pas in beeld horen te komen
 * @param {Function}     [opties.naSplit]
 */
export function onthulRegels(element, opties = {}) {
  if (!element || minderBeweging()) {
    return;
  }

  const {
    klasse = 'hero-line',
    delay = 0,
    duration = 1.1,
    stagger = 0.1,
    fade = false,
    scrollTrigger = null,
    naSplit = null,
  } = opties;

  // Meteen onzichtbaar, want het splitsen komt pas als het lettertype binnen is.
  gsap.set(element, { autoAlpha: 0 });

  const herstel = () => gsap.set(element, { clearProps: 'all' });

  lettertypenGereed()
    .then(() => {
      SplitText.create(element, {
        type: 'lines',
        mask: 'lines',
        linesClass: klasse,
        autoSplit: true,
        onSplit(self) {
          if (!self.lines.length) {
            // Geen regels om te animeren: dan hoort de kop er gewoon te staan.
            herstel();
            return null;
          }

          // Pas hier weer aan: de regels staan nu onder hun masker, dus er valt niets
          // te zien van het opbouwen.
          gsap.set(element, { autoAlpha: 1 });

          const animatie = gsap.fromTo(
            self.lines,
            { yPercent: 115, ...(fade ? { opacity: 0 } : {}) },
            {
              yPercent: 0,
              ...(fade ? { opacity: 1 } : {}),
              duration,
              ease: EASE,
              stagger,
              delay,
              ...(scrollTrigger ? { scrollTrigger } : {}),
            }
          );

          if (naSplit) {
            naSplit(element);
          }

          return animatie;
        },
      });
    })
    .catch(herstel);
}
